// src/pages/Login.jsx
// Accesso alla piattaforma con utente e password, verificati dal server.
// Ogni copia di RaceSense Live (la nostra e quelle dei clienti) ha i suoi
// account: la pagina mostra il nome della copia su cui si sta entrando.
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logoRaceSenseLive from '../img/logo-racesense-live.svg';
import { setSession, getSession } from '../session';
import './Login.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const [username, setUsername] = useState('');
    const [pw, setPw] = useState('');
    const [err, setErr] = useState('');
    const [loading, setLoading] = useState(false);
    const [instance, setInstance] = useState(null);   // { id, label }

    // Dove tornare dopo l'accesso: ?next=... oppure la pagina di provenienza.
    const params = new URLSearchParams(location.search);
    const nextRaw = params.get('next') || location.state?.from?.pathname || '/';
    const next = nextRaw.startsWith('/') && !nextRaw.startsWith('//') && !nextRaw.startsWith('/login') ? nextRaw : '/';

    // Già dentro: niente login.
    useEffect(() => {
        if (getSession()) navigate(next, { replace: true });
    }, [navigate, next]);

    useEffect(() => {
        let alive = true;
        fetch(`${API_BASE}/api/instance`)
            .then(r => (r.ok ? r.json() : null))
            .then(j => { if (alive && j) setInstance(j); })
            .catch(() => { });
        return () => { alive = false; };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!username.trim() || !pw) {
            setErr('Inserisci utente e password');
            return;
        }
        setLoading(true);
        setErr('');
        try {
            const r = await fetch(`${API_BASE}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: username.trim(), password: pw }),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok || !j.token) {
                setErr(j.error || 'Accesso non riuscito');
                setLoading(false);
                return;
            }
            setSession({ token: j.token, user: j.user, instance: j.instance, links: [] });
            navigate(next, { replace: true });
        } catch {
            setErr('Server non raggiungibile. Riprova tra poco.');
            setLoading(false);
        }
    };

    const isClientCopy = instance && instance.id !== 'racesense';

    return (
        <div className="login-page">
            <div className="login-bg-grid" />
            <div className="login-bg-glow" />

            <div className="login-container">
                <div className="login-logo-wrapper">
                    <img src={logoRaceSenseLive} alt="RaceSense Live" className="login-logo" />
                    {isClientCopy && <span className="login-instance">{instance.label}</span>}
                </div>

                <div className="login-card">
                    <div className="login-card-header">
                        <div className="login-icon-wrapper">
                            <svg className="login-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                <circle cx="12" cy="16" r="1" />
                            </svg>
                        </div>
                        <h1 className="login-title">Accedi</h1>
                        <p className="login-subtitle">
                            {isClientCopy ? `Area riservata ${instance.label}` : 'Inserisci le tue credenziali per entrare nella piattaforma'}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-form" autoComplete="on">
                        <div className="login-input-wrapper">
                            <div className="login-input-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                name="username"
                                placeholder="Utente"
                                autoComplete="username"
                                autoCapitalize="none"
                                spellCheck="false"
                                value={username}
                                onChange={(e) => { setUsername(e.target.value); setErr(''); }}
                                className={`login-input ${err ? 'login-input--error' : ''}`}
                                autoFocus
                                disabled={loading}
                            />
                        </div>

                        <div className="login-input-wrapper">
                            <div className="login-input-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                                </svg>
                            </div>
                            <input
                                type="password"
                                name="password"
                                placeholder="Password"
                                autoComplete="current-password"
                                value={pw}
                                onChange={(e) => { setPw(e.target.value); setErr(''); }}
                                className={`login-input ${err ? 'login-input--error' : ''}`}
                                disabled={loading}
                            />
                        </div>

                        {err && (
                            <div className="login-error" role="alert">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="8" x2="12" y2="12" />
                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                <span>{err}</span>
                            </div>
                        )}

                        <button type="submit" className={`login-btn ${loading ? 'login-btn--loading' : ''}`} disabled={loading}>
                            {loading ? (
                                <div className="login-spinner" />
                            ) : (
                                <>
                                    <span>Accedi</span>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                        <polyline points="12 5 19 12 12 19" />
                                    </svg>
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
